import React, { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import useSWR from "swr";
import { noAuthfetcher } from "../../Api/swrConfig";
import { NewsApiUrls } from "../../Api/Auth/ApiUrls";
import NotFound from "../Notfound";
import HeroSection from "../../Components/Common/HeroSection";
import Contact from "../Home/Components/Contact";
import { motion } from "framer-motion";
import { Loader } from "../../Components/Common/Loader";
import { Helmet } from "react-helmet-async";

const NewsItem = () => {
  const { slug } = useParams();
  const location = useLocation();

  const { data, isLoading } = useSWR(NewsApiUrls.single(slug), noAuthfetcher);

  const seoInfo = useMemo(() => {
    return {
      title: `${data?.data?.title || "Road to FE"} | RoadtoFE News & Insights`,
      description:
        "Stay informed with RoadtoFE's latest news, blog posts, and expert insights on FE exam preparation. Discover tips, updates, success stories, and more.",
      keywords: "FE exam blog, FE exam tips, FE exam news, RoadtoFE articles",
    };
  }, [data?.data?.title]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    // This ensures the effect runs after each route change

    // Directly manipulate document head as a fallback
    document.title = seoInfo.title;

    // Update meta tags
    let descMeta = document.querySelector('meta[name="description"]');
    if (!descMeta) {
      descMeta = document.createElement("meta");
      descMeta.setAttribute("name", "description");
      document.head.appendChild(descMeta);
    }
    descMeta.setAttribute("content", seoInfo.description);

    // Keywords meta
    if (seoInfo.keywords) {
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      if (!keywordsMeta) {
        keywordsMeta = document.createElement("meta");
        keywordsMeta.setAttribute("name", "keywords");
        document.head.appendChild(keywordsMeta);
      }
      keywordsMeta.setAttribute("content", seoInfo.keywords);
    }
  }, [location.pathname, seoInfo, isLoading]);

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (!data) {
    return <NotFound />;
  }

  return (
    <>
      <Helmet key={slug}>
        <title>{seoInfo.title}</title>
        <meta name="description" content={seoInfo.description} />
        <meta name="keywords" content={seoInfo.keywords} />
      </Helmet>
      <motion.div
        initial={{ opacity: 1, y: -40 }} // Start above the view
        whileInView={{ opacity: 1, y: 0 }} // End at normal position
        transition={{ duration: 1, ease: "easeOut" }} // Longer transition for more emphasis
      >
        <HeroSection
          title={data?.data?.title}
          backgroundImage="https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif"
        />
        <div className="gap-3 container mx-auto sm:mt-12 mt-4 flex justify-center md:justify-between items-start flex-wrap md:flex-nowrap px-2">
          <div className="max-w-5xl w-full prose prose-neutral">
            <div
              dangerouslySetInnerHTML={{
                __html: data?.data?.description || "",
              }}
            />
          </div>
          <div>
            <img
              src={data?.data?.link}
              alt="News"
              className="w-full h-72 object-cover rounded-md"
            />
          </div>
        </div>
        <Contact />
      </motion.div>
    </>
  );
};

export default NewsItem;
